import LocationLanding from './LocationLanding';
import { buildBreadcrumb, buildFAQPage, SITE_URL } from '@/lib/seo';

const SLUG = 'ambulance-beaulon-garnat';
const CANONICAL = `${SITE_URL}/${SLUG}`;

const COMMUNES = [
  { name: 'Garnat-sur-Engièvre', lat: 46.5806, lng: 3.6819, main: true },
  { name: 'Beaulon',              lat: 46.5978, lng: 3.7803, main: true },
  { name: 'Coulanges',            lat: 46.5644, lng: 3.7061 },
  { name: 'Chevagnes',            lat: 46.5642, lng: 3.6069 },
  { name: 'Paray-le-Frésil',      lat: 46.6042, lng: 3.6317 },
  { name: 'Thiel-sur-Acolin',     lat: 46.5644, lng: 3.6789 },
  { name: 'Molinet',              lat: 46.4508, lng: 3.9558 },
  { name: 'Dompierre-sur-Besbre', lat: 46.5225, lng: 3.6836 },
];

const FAQS = [
  { q: 'Comment réserver un transport sanitaire avec Ambulances Beaulon à Garnat-sur-Engièvre ?', a: `Appelez directement notre équipe au 04 70 42 72 63 ou remplissez le formulaire ci-dessus. Nous vous rappelons pour confirmer les horaires, le mode de transport et les modalités administratives.` },
  { q: 'Quelles communes desservez-vous depuis Garnat-sur-Engièvre ?', a: `Nous couvrons principalement Garnat-sur-Engièvre, Beaulon, Chevagnes, Paray-le-Frésil, Thiel-sur-Acolin, Coulanges, Molinet et Dompierre-sur-Besbre. Nous intervenons plus largement sur tout le nord-est du département de l'Allier selon disponibilité.` },
  { q: 'Une prescription médicale est-elle obligatoire pour un remboursement CPAM ?', a: `Oui. Pour être remboursé par l'Assurance Maladie, une prescription médicale (bon de transport CERFA n°50742*04) délivrée par votre médecin est obligatoire, sauf en cas d'urgence vitale. Elle indique le mode de transport à utiliser (ambulance, VSL, taxi conventionné).` },
  { q: 'Ambulances Beaulon Garnat est-il conventionné CPAM ?', a: `Oui. Tous nos véhicules — ambulances, VSL et taxis — sont conventionnés par l'Assurance Maladie. Nous appliquons la prise en charge directe (tiers payant) pour les patients éligibles.` },
  { q: 'Quels services proposez-vous ?', a: `Transport en ambulance, en VSL et en taxi conventionné. Hospitalisations, consultations, dialyses, séances de chimiothérapie et radiothérapie, rééducation, sorties d'hôpital, transferts inter-établissements et longue distance. Transport bariatrique disponible sur demande.` },
  { q: 'Peut-on être accompagné pendant le trajet ?', a: `Oui. Un accompagnant peut voyager avec vous (généralement un proche). Merci de le préciser lors de la réservation pour que nous prévoyions la place nécessaire.` },
  { q: 'Comment nous joindre en cas de transport à organiser rapidement ?', a: `Composez le 04 70 42 72 63 pour organiser un transport programmé. En cas d\'urgence vitale, appelez immédiatement le 15 (SAMU) — Ambulances Beaulon est un service de transport sanitaire programmé, pas un service public d\'urgence.` },
];

const CONFIG = {
  slug: SLUG,
  brandName: 'Ambulances Beaulon – Garnat-sur-Engièvre',
  brandNameShort: 'Ambulances Beaulon',
  brandTitle: 'Ambulances Beaulon',
  brandSubtitle: 'Garnat-sur-Engièvre',
  kicker: 'Notre implantation historique dans le nord-est de l\'Allier',
  zoneLabel: 'Garnat-sur-Engièvre · Beaulon · nord-est Allier',
  zoneLabelShort: 'Garnat / Beaulon',
  heroTitle: (
    <>Ambulance et VSL à <span className="text-brand-700">Garnat-sur-Engièvre</span> et <span className="text-brand-700">Beaulon</span></>
  ),
  description: (
    <>Notre implantation historique. Transport sanitaire conventionné pour <strong className="text-anthracite-900">consultations, hospitalisations, dialyses, rééducation, sorties d'hôpital</strong> et transports programmés dans le nord-est du département de l'Allier.</>
  ),
  phoneDisplay: '04 70 42 72 63',
  phoneTel: '+33470427263',
  whyChooseTitle: 'Notre équipe historique, présente dans le nord-est de l\'Allier depuis toujours',
  serviceAreaTitle: 'Nous intervenons sur tout le nord-est de l\'Allier',
  serviceAreaDesc: 'Garnat-sur-Engièvre, Beaulon, Chevagnes, Paray-le-Frésil, Thiel-sur-Acolin, Coulanges, Molinet, Dompierre-sur-Besbre et communes environnantes. Nous intervenons plus largement dans le nord-est du département de l\'Allier selon disponibilité.',
  communes: COMMUNES,
  mapCenter: [46.5806, 3.6819],
  mapRadius: 20000,
  mapZoom: 10,
  canonical: CANONICAL,
  ogImage: 'https://customer-assets.emergentagent.com/job_ambulances-beaulon/artifacts/5zctf2x4_file_000000005b88720a8aa856385a7438f6.png',
  ogType: 'website',
  seoTitle: 'Ambulance Garnat-sur-Engièvre & Beaulon | Ambulances Beaulon Allier',
  seoDescription: 'Ambulance et VSL à Garnat-sur-Engièvre et Beaulon. Notre implantation historique. Transport sanitaire conventionné CPAM dans le nord-est de l\'Allier. Réservez au 04 70 42 72 63.',
  seoKeywords: 'ambulance garnat-sur-engièvre, ambulance beaulon, vsl garnat, vsl beaulon, transport sanitaire garnat, transport sanitaire beaulon, ambulance conventionnée allier, transport médical allier, taxi conventionné garnat, transport dialyse allier, transport chimiothérapie allier',
  jsonLd: [
    {
      '@context': 'https://schema.org',
      '@type': ['MedicalBusiness', 'LocalBusiness'],
      '@id': `${CANONICAL}#business`,
      name: 'Ambulances Beaulon – Garnat-sur-Engièvre',
      alternateName: ['Ambulance Beaulon', 'Ambulances Beaulon'],
      description: 'Transport sanitaire conventionné en ambulance, VSL et taxi à Garnat-sur-Engièvre, Beaulon et dans tout le nord-est de l\'Allier. Notre implantation historique.',
      url: CANONICAL,
      telephone: '+33470427263',
      priceRange: '€€',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Garnat-sur-Engièvre',
        postalCode: '03230',
        addressRegion: 'Allier',
        addressCountry: 'FR',
      },
      geo: { '@type': 'GeoCoordinates', latitude: 46.5806, longitude: 3.6819 },
      areaServed: COMMUNES.map((c) => ({ '@type': 'City', name: c.name })),
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'],
        opens: '00:00', closes: '23:59',
      }],
      parentOrganization: { '@type': 'Organization', name: 'Groupe Ambulances Beaulon', url: SITE_URL },
      medicalSpecialty: 'MedicalTransport',
    },
    buildBreadcrumb([
      { name: 'Accueil', url: SITE_URL + '/' },
      { name: 'Nos implantations', url: SITE_URL + '/implantations' },
      { name: 'Ambulances Beaulon – Garnat-sur-Engièvre', url: CANONICAL },
    ]),
    buildFAQPage(FAQS),
  ],
};

export default function AmbulanceBeaulonGarnat() {
  return <LocationLanding config={CONFIG} />;
}
